import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthenticatedRoutes } from '@/router/routes';

import { useCreateReport } from '../hooks/use-create-report';

interface CreateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateReportDialog = ({ open, onOpenChange }: CreateReportDialogProps) => {
  const { t } = useTranslation('authenticated.reports');
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'ORG' | 'PRIVATE'>('ORG');

  const { mutate: createReport, isPending } = useCreateReport();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    createReport(
      { name: name.trim(), scope },
      {
        onSuccess: report => {
          onOpenChange(false);
          setName('');
          setScope('ORG');
          navigate(AuthenticatedRoutes.ReportDetails.replace(':reportId', report.id));
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('createReport.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="report-name">{t('createReport.nameLabel')}</Label>
            <Input
              id="report-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('createReport.namePlaceholder')}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('createReport.scopeLabel')}</Label>
            <div className="flex gap-2">
              {/* eslint-disable-next-line i18next/no-literal-string */}
              {(['ORG', 'PRIVATE'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${
                    scope === s
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {t(`scope.${s}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{t(`scope.${scope}Description`)}</p>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {t('createReport.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
